// src/hooks/useDocumentDownload.ts
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosError, AxiosResponse } from 'axios';
import { ROUTE_PATHS } from '../routes/routePaths';
/**
 * Reusable hook to handle document download when a mutation returns a blob.
 * - Triggers file download with correct filename
 * - Handles Content-Disposition parsing
 * - Navigates to error page on failure
 */
export function useDocumentDownload({
  storageMutation,
  lastRequestedFilename,
  setLastRequestedFilename,
  skipErrorHandling = false,
}: {
  storageMutation: any;
  lastRequestedFilename: string | null;
  setLastRequestedFilename: React.Dispatch<React.SetStateAction<string | null>>;
  skipErrorHandling?: boolean;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    if (storageMutation.isSuccess && storageMutation.data) {
      const response = storageMutation.data as AxiosResponse<Blob>;
      const blob = response.data;

      // Extract filename from headers
      const cd =
        response.headers['content-disposition'] ??
        response.headers['Content-Disposition'] ??
        '';
      const filenameMatch = cd.match(
        /filename\*=utf-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i
      );

      const downloadName =
        (filenameMatch &&
          decodeURIComponent(
            filenameMatch[1] ?? filenameMatch[2] ?? filenameMatch[3]
          )) ||
        lastRequestedFilename ||
        'download';

      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.append(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Dispatch custom event for PostHog tracking (before cleanup)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('documentDownloadSuccess'));
      }

      // Cleanup
      setLastRequestedFilename(null);
      storageMutation.reset?.();
    }

    if (storageMutation.isError && !skipErrorHandling) {
      const error = storageMutation.error as AxiosError | undefined;
      const status = error?.response?.status;
      if (status === 403) {
        const url = `${globalThis.location.origin}${ROUTE_PATHS.ACCESS_ERROR}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        setLastRequestedFilename(null);
        storageMutation.reset?.();
        return;
      }
      const errorUrl = `${globalThis.location.origin}/standard-error?isDocumentError=true`;
      window.open(errorUrl, '_blank', 'noopener,noreferrer');
      setLastRequestedFilename(null);
      storageMutation.reset?.();
    }
  }, [
    storageMutation,
    lastRequestedFilename,
    navigate,
    setLastRequestedFilename,
    skipErrorHandling,
  ]);
}
